import PIEModal from '@src/shared/components/PIEModal';
import React, { useCallback, useEffect, useMemo } from 'react';
import PopupImage from '@src/assets/ocf-modal-image.png';
import styles from './OCFTutorialPopup.module.scss';
import PIEText from '@src/shared/components/PIEText';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import PIEList from '@src/shared/components/PIEList';
import {
  IconAppSuccess,
  IconChartIncrease,
  IconPlay,
  IconStopwatch,
} from '@justeattakeaway/pie-icons-react';
import PIEButton from '@src/shared/components/PIEButton';
import { useTranslation } from 'react-i18next';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import dayjs from 'dayjs';

interface componentProps {
  onTutorialStart: () => void;
  onTutorialSkip?: () => void;
}

const OCFTutorialPopup = ({ onTutorialStart, onTutorialSkip }: componentProps) => {
  const [showModal, setShowModal] = React.useState(true);
  const { t } = useTranslation();
  const { isResolutionMobile } = useDeviceDetect();
  const [shownDays, setShownDays] = React.useState<string[]>([]);

  useEffect(() => {
    const localStorageValue = localStorage.getItem('ocf_tutorial_popup_seen');
    const shownDays = localStorageValue ? JSON.parse(localStorageValue) : [];
    setShownDays(shownDays);
  }, []);
  const isModalShowable = () => {
    if (shownDays.length >= 2 || shownDays.includes(dayjs().format('YYYY-MM-DD'))) {
      return false;
    } else {
      return true;
    }
  };

  const onCloseHandler = useCallback(() => {
    setShowModal(false);
    if (!shownDays.includes(dayjs().format('YYYY-MM-DD'))) {
      shownDays.push(dayjs().format('YYYY-MM-DD'));
    }
    localStorage.setItem('ocf_tutorial_popup_seen', JSON.stringify(shownDays));
    onTutorialSkip?.();
  }, [shownDays]);

  if (!isModalShowable()) {
    return null;
  }

  return (
    <PIEModal isOpen={showModal} size="small" onAfterClose={onCloseHandler}>
      <PIEModal.Header onClose={onCloseHandler} heading={<></>} />

      <PIEModal.Content modNoPadding={!isResolutionMobile}>
        <PIEComponentUtil marginBottom={'var(--xds-spacing-d)'}>
          <div className={styles.popupImageContainer}>
            <img src={PopupImage} width={150} height={150} />
          </div>
          <PIEText
            align="center"
            size={'var(--xds-font-size-24)'}
            fontWeight={'var(--xds-font-weight-extrabold)'}
            text={t('ocf_screens.OCF_TUTORIAL_POPUP_TITLE')}
          />
          <PIEComponentUtil marginX="var(--xds-spacing-e)">
            <PIEList modBorder={false}>
              <PIEList.Item left={<IconChartIncrease size="s" />}>
                {t('ocf_screens.OCF_TUTORIAL_POPUP_BENEFIT_1_CONTENT')}
              </PIEList.Item>
              <PIEList.Item left={<IconAppSuccess size="s" />}>
                {t('ocf_screens.OCF_TUTORIAL_POPUP_BENEFIT_2_CONTENT')}
              </PIEList.Item>
              <PIEList.Item left={<IconStopwatch size="s" />}>
                {t('ocf_screens.OCF_TUTORIAL_POPUP_BENEFIT_3_CONTENT')}
              </PIEList.Item>
            </PIEList>
          </PIEComponentUtil>
        </PIEComponentUtil>
      </PIEModal.Content>
      <PIEModal.Footer>
        <div
          style={
            isResolutionMobile
              ? {
                  display: 'flex',
                  flexDirection: 'column-reverse',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                }
              : {
                  display: 'inline-flex',
                }
          }
        >
          <PIEComponentUtil marginRight={isResolutionMobile ? 'var(--xds-spacing-b)' : ''}>
            <PIEButton
              autoWidth
              size="medium"
              mode="ghost"
              type="button"
              text={t('ocf_screens.OCF_TUTORIAL_POPUP_SEE_LATER_BUTTON')}
              onClick={() => {
                setShowModal(false);
                onTutorialSkip?.();
              }}
            ></PIEButton>
          </PIEComponentUtil>
          <PIEComponentUtil marginBottom={isResolutionMobile ? 'var(--xds-spacing-b)' : ''}>
            <PIEButton
              autoWidth
              onClick={() => {
                setShowModal(false);
                onTutorialStart?.();
              }}
              icon={<IconPlay size="m" />}
              size="medium"
              mode="primary"
              type="button"
              text={t('ocf_screens.OCF_TUTORIAL_POPUP_SHOW_ME_AROUND_BUTTON')}
            ></PIEButton>
          </PIEComponentUtil>
        </div>
      </PIEModal.Footer>
    </PIEModal>
  );
};

export default OCFTutorialPopup;
